/**
 * Admin: Select which CMS page is shown as the homepage.
 */

import { useEffect, useState } from 'react';
import { getHomeSetting, getAllCMSPages, setHomePage, clearHomePage, type CMSPage, type HomeSetting } from '@/lib/cms';

interface AdminHomeSelectorProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  /** Optional action (e.g. Help & guides) to show in the title row. */
  titleAction?: React.ReactNode;
}

export function AdminHomeSelector({
  currentUserId,
  showNotify,
  titleAction,
}: AdminHomeSelectorProps): JSX.Element {
  const [homeSetting, setHomeSetting] = useState<HomeSetting | null>(null);
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [home, list] = await Promise.all([getHomeSetting(), getAllCMSPages()]);
      setHomeSetting(home ?? null);
      setPages(list);
    } catch (err) {
      console.error('[AdminHomeSelector] Load error:', err);
      showNotify?.('Failed to load home setting.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable, run once on mount
  }, []);

  const currentPageId = homeSetting?.pageId ?? null;

  const setHome = async (page: CMSPage) => {
    try {
      await setHomePage({ pageId: page.id }, currentUserId);
      showNotify?.('Home page updated.');
      setHomeSetting({ pageId: page.id, updatedBy: currentUserId });
    } catch (err) {
      console.error('[AdminHomeSelector] Set home error:', err);
      showNotify?.('Failed to set home page.', 'error');
    }
  };

  const removeHome = async () => {
    try {
      await clearHomePage();
      showNotify?.('Home page cleared. Site root will show the default home page.');
      setHomeSetting(null);
    } catch (err) {
      console.error('[AdminHomeSelector] Clear home error:', err);
      showNotify?.('Failed to clear home page.', 'error');
    }
  };

  if (loading) {
    return <div className="text-gray-600">Loading…</div>;
  }

  const homeCandidates = pages.filter((p) => p.type === 'home' && p.status === 'published');
  const allPublished = pages.filter((p) => p.status === 'published');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Set home page</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Choose which page is shown at the site root (/). Only pages with type &quot;Home&quot; and status &quot;Published&quot; are listed below.
          </p>
        </div>
        {titleAction}
      </div>
      {homeCandidates.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
          No published home pages yet. Create a page with type &quot;Home&quot; and set status to &quot;Published&quot; in CMS Pages.
        </div>
      ) : (
        <ul className="space-y-2">
          {homeCandidates.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <span className="font-medium text-gray-900">{p.title}</span>
              <span className="text-sm text-gray-500">{p.slug}</span>
              <div className="flex items-center gap-2">
                {currentPageId === p.id && (
                  <button
                    type="button"
                    onClick={removeHome}
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setHome(p)}
                  disabled={currentPageId === p.id}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentPageId === p.id ? 'Current home' : 'Set as home'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {currentPageId && !homeCandidates.some((p) => p.id === currentPageId) && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            A CMS page is currently set as the home page but it is no longer in the list above (e.g. unpublished or type changed). You can clear it to use the default home page.
          </p>
          <button
            type="button"
            onClick={removeHome}
            className="mt-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Remove and use default home
          </button>
        </div>
      )}
      {allPublished.length > 0 && homeCandidates.length === 0 && (
        <p className="text-sm text-gray-500">
          You have {allPublished.length} published page(s) but none with type &quot;Home&quot;. Edit a page and set its type to &quot;Home&quot; to use it as the homepage.
        </p>
      )}
    </div>
  );
}
